import { Icon } from '../common';
import { anomalyWord, countByReason, saysAllClear, viewingWord, type AttentionItem } from './needsAttention';

// The command strip's first segment (daily-command-strip AC4–AC7).
//
// THE IRREDUCIBLE CORE is the warning glyph and the separator. They appear at
// every width, in every day-state, loading included, and are never traded away
// — they are what makes the segment recognisable as the same thing from one day
// to the next rather than an unexplained tick.
//
// There is NO TOTAL across the anomaly kinds. A booking that is both
// unvaccinated and awaiting a deposit is counted under both badges — two
// telephone calls, to two different people — so a total standing beside them
// could never agree with them.

export interface AttentionSegmentProps {
  items: AttentionItem[];
  // Viewings still EXPECTED in the look-ahead window — a COUNT, not the rows.
  // The segment's job is to display a figure and a word; which records produced
  // it is the caller's business, and handing it the rows would give it a second
  // shape to know about for no gain.
  //
  // ITS OWN AXIS, deliberately. A viewing is never `unconfirmed` — it has its
  // own vocabulary — so it cannot land on the stays' counter. That is the
  // double count this design removed: a viewing borrowing a booking status
  // would have been counted once here and once there while the panel beneath
  // listed it once.
  viewings?: number;
  // The 7-day look-ahead has not resolved. The segment holds its shape and says
  // NOTHING about the day: claiming "nothing to chase" before we know is the one
  // message a user would act on by not acting.
  loading?: boolean;
  // Held items are for a previous anchor — kept rather than blanked (blanking
  // flickered on every date step), but dimmed so the bar never looks settled
  // while quietly answering yesterday's question.
  stale?: boolean;
  open: boolean;
  onToggle: () => void;
}

export function AttentionSegment({ items, viewings = 0, loading, stale, open, onToggle }: AttentionSegmentProps) {
  const { vaccinations, unconfirmed } = countByReason(items);

  // THREE KINDS, THREE SLOTS: unconfirmed, vaccines, viewings — what the bar is reserved for (Freddi,
  // 2026-10-14). Requests left it for the stack beside "Daily" (notifications-daily-stack), so every
  // kind now says itself; the fold below only ever runs if a fourth kind is added.
  //
  // THE APP'S OWN WORDS. "unconfirmed" is the booking's actual status;
  // "vaccines" is Sir's own word (2026-08-26) — see anomalyWord.
  const all = [
    { n: unconfirmed, icon: 'hourglass', cls: 'unc', word: anomalyWord('unconfirmed', unconfirmed) },
    { n: vaccinations, icon: 'vaccine', cls: 'vax', word: anomalyWord('vaccinations', vaccinations) },
    { n: viewings, icon: 'eye', cls: 'vw', word: viewingWord(viewings) },
  ].filter((k) => k.n > 0);
  const own = (k: (typeof all)[number]) => ({
    ...k,
    title: `${k.n} ${k.word}`,
    label: `${k.n} ${k.word}`,
    // CAPPED FOR DISPLAY; the true figure stays in the title and aria-label.
    shown: k.n > 99 ? '99+' : String(k.n),
  });
  const folded = (rest: typeof all) => {
    const names = rest.map((k) => `${k.n} ${k.word}`);
    return {
      n: rest.length, icon: null as string | null, cls: 'more', word: 'others',
      title: names.join(' · '), label: `${rest.length} other kinds: ${names.join(', ')}`, shown: `+${rest.length}`,
    };
  };
  const kinds = all.length <= 3 ? all.map(own) : [...all.slice(0, 2).map(own), folded(all.slice(2))];
  // NOTHING ON THE BAR is a fact about what to DRAW. Whether the day may be
  // CALLED clear — the title's all-clear — is saysAllClear's alone, and it is
  // handed the viewings as their own excuse. Folding the viewings into a
  // `clear` computed here and passing both made that excuse dead code at its
  // only call site: the function could never see a viewing the caller had not
  // already zeroed out (review, PR #173).
  const quiet = kinds.length === 0;
  // Nothing behind it on a quiet day, and nothing known yet while loading — so
  // it stops being a control rather than offering a door onto a room nobody has
  // looked in. But an OPEN trigger always stays closable: resolving the last
  // item in the modal empties the list while the panel is still open, and
  // disabling the trigger then left a panel nothing could shut (PR #164, codex).
  const shut = (quiet || loading) && !open;
  // EVERY COUNTER NAMES ITSELF, at every count and however many kinds are on the
  // bar (counters-become-a-readout AC1/AC2). The word used to be a lone
  // counter's privilege — a second kind arrived and both fell silent, leaving
  // two bare numbers behind two glyphs a reader had to have learnt. That rule
  // was a width trade, made when the segment had 211px; the permanently
  // collapsed rail gives it 396, and three worded counters need 325 at their
  // widest. There is no
  // longer anything to trade, so daily-command-strip AC6 is superseded rather
  // than narrowed.
  //
  // The cap is a DISPLAY rule and never a reason to stop speaking: "99+" is
  // exactly the state where a bare figure explains itself least.

  return (
    <div className={'cs-seg cs-seg-attn' + (stale ? ' is-stale' : '')}>
      <button
        type="button"
        className="cs-seg-btn"
        onClick={shut ? undefined : onToggle}
        disabled={shut}
        aria-expanded={shut ? undefined : open}
        title={
          // saysAllClear, not a condition written out here. `items` is empty
          // before the look-ahead lands, after it fails, AND while the items on
          // hand still belong to the previous day — and this claimed the week
          // was in order in the first two cases, then in the third (PR #164,
          // codex + claude). The rule is named and tested precisely because it
          // keeps being got wrong one excuse at a time.
          saysAllClear({ clear: items.length === 0, loading, stale, viewings })
            ? 'Nothing to chase — every arrival in the next 7 days is confirmed, with a vaccination date on file, and nobody is coming to look round'
            : undefined
        }
      >
        <span className="cs-attn-ico" aria-hidden="true">
          <Icon name="alert-triangle" size={15} />
        </span>
        <span className="cs-sep" aria-hidden="true" />

        {loading ? (
          <>
            <span className="cs-sk cs-sk-badge" aria-hidden="true" />
            <span className="cs-sk cs-sk-badge" aria-hidden="true" />
            <span className="sr-only">Checking the week ahead</span>
          </>
        ) : quiet ? (
          <span className="cs-clear">
            <span className="cs-tick" aria-hidden="true">
              <Icon name="check" size={12} />
            </span>
            {/* The ellipsis is Freddi's, and it is load-bearing rather than
                decorative: it is this sentence, at its full length, that sets
                the segment's fixed width (see .cs-seg-attn in 05-today.css).
                Typographic … , which is what the other 38 user-facing strings
                in this app use. */}
            <span className="cs-clear-ln">Nothing to chase…</span>
          </span>
        ) : (
          <>
            {/* ONE BOX FOR THE COUNTERS, so they can share the segment's fixed
                width rather than run left and leave a hole before the chevron.
                Fixing the box was only half the job: the width stopped changing
                and the contents had not learnt to use it, so two anomalies sat
                shoulder to shoulder against 25px of nothing (Freddi,
                2026-08-17 — they "look squashed"). */}
            <span className="cs-cnts">
              {kinds.map((k) => (
                <span
                  key={k.cls}
                  className={'cs-cnt ' + k.cls}
                  title={k.title}
                  aria-label={k.label}
                >
                  {k.icon && <Icon name={k.icon} size={12} />}
                  {/* CAPPED FOR DISPLAY. The count has no ceiling — it is
                      whatever the seven-day window holds — and a fixed box has
                      to be sized for something. Three digits still fit; four
                      would clip, and a clipped number is worse than an honest
                      "more than 99" on a segment whose job is to be glanced at.
                      The true figure stays in the title and the aria-label. */}
                  <b>{k.shown}</b>
                  <span className="cs-cnt-word">{k.word}</span>
                </span>
              ))}
            </span>
            <span className="cs-chev" aria-hidden="true">
              <Icon name="chevron-down" size={13} />
            </span>
          </>
        )}
      </button>
    </div>
  );
}
